import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';
import { accountKindForMethod } from '@/lib/contribution-methods';

interface AccountItem {
  id: string;
  name: string;
  currency: string;
  kind: string;
}

export const ContributionAccountField = ({
  open,
  method,
  currency,
  value,
  onChange,
}: {
  open: boolean;
  method: string;
  currency: string;
  value: string | null;
  onChange: (accountId: string | null) => void;
}) => {
  const { t } = useTranslation('giving');
  const kind = accountKindForMethod(method);

  const accounts = useQuery({
    queryKey: ['financial-position', 'items', 'gift-account', kind],
    queryFn: () =>
      api.get<{ data: AccountItem[] }>(`/financial-position/items?kind=${kind}`),
    enabled: open && Boolean(kind),
  });

  if (!kind) return null;

  const options = (accounts.data?.data ?? [])
    .filter((account) => account.currency === currency)
    .map((account) => ({ value: account.id, label: account.name }));

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{t('form.account')}</Label>
      <Listbox
        value={value}
        onChange={(next) => onChange(next)}
        options={options}
        placeholder={
          options.length === 0 ? t('form.accountEmpty') : t('form.accountPlaceholder')
        }
        popoverPortal="dialog"
        disabled={accounts.isLoading || options.length === 0}
      />
    </div>
  );
};
